/**
 * What the Home time cards show and when: the evening review after the
 * person's review time (or from its notification link), and the carry-over
 * while an earlier day is unreleased.
 */
import { timeActions } from "../actions";
import { releaseBlock } from "../model";
import type {
  BriefSuggestion,
  TimeEntry,
  TimeNudge,
  TodaySummary,
  UnreleasedDay
} from "../types";

export const REVIEW_PARAM = "time_review";

/** An admin's nudge to release time, in a line. */
export const nudgeLine = (nudge: TimeNudge | null | undefined): string | null =>
  nudge
    ? `${nudge.by_name || "Billing"} asked you to release your time · ${
        nudge.count
      } ${nudge.count === 1 ? "entry" : "entries"} waiting`
    : null;

/** Stable across brief refreshes, which renumber suggestion ids. */
export const suggestionKey = (
  s: Pick<BriefSuggestion, "kind" | "entry_ids">
): string => `${s.kind}:${[...s.entry_ids].sort().join(",")}`;

/** "18:00" as "6:00 PM". */
export const reviewTimeLabel = (hhmm: string): string => {
  const [h, m] = hhmm.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};

/**
 * The day the evening card reviews, or null when it isn't due. A notification
 * link names the day; otherwise today, once its review time has passed.
 */
export const eveningReviewDate = (
  today: TodaySummary | undefined,
  linked: string | null
): string | null => {
  if (linked) return linked;
  if (!today || !today.evening_review || !today.review_open) return null;
  return today.totals.count > 0 ? today.today : null;
};

/** Earlier unreleased days, without the one the evening card already shows. */
export const carryOverDays = (
  today: TodaySummary | undefined,
  reviewing: string | null
): UnreleasedDay[] =>
  (today?.earlier_unreleased ?? []).filter((d) => d.work_date !== reviewing);

export const carryTitle = (days: UnreleasedDay[]): string => {
  if (!days.length) return "";
  const oldest = days[0].weekday;
  const later = days.length - 1;
  return later
    ? `${oldest} and ${later} later ${
        later === 1 ? "day" : "days"
      } aren't released yet`
    : `${oldest} isn't released yet`;
};

export const isOpen = (e: TimeEntry): boolean =>
  e.unreleased && e.status !== "discarded";

/** Checked, ready to release, and still unreleased. */
export const readyToRelease = (
  entries: TimeEntry[],
  unchecked: ReadonlySet<string>
): TimeEntry[] =>
  entries.filter((e) => isOpen(e) && !releaseBlock(e) && !unchecked.has(e.id));

/** The entry's own billable activity, for the row's second line. */
export const activityLine = (e: TimeEntry): string =>
  (e.activity ?? [])
    .filter((a) => a.billable && !a.agent_kind)
    .map((a) => a.title)
    .filter(Boolean)
    .slice(0, 4)
    .join(" · ");

/** Run a brief suggestion through the normal entry endpoints. */
export const applySuggestion = async (s: BriefSuggestion): Promise<boolean> => {
  const [first] = s.entry_ids;
  switch (s.kind) {
    case "assign_case":
      return Boolean(
        s.individual_id && (await timeActions.assign(first, s.individual_id))
      );
    case "merge":
      return Boolean(await timeActions.merge(s.entry_ids));
    case "rewrite_narrative":
      return Boolean(
        await timeActions.update(first, { narrative: s.narrative ?? "" })
      );
    case "set_hours":
      return Boolean(
        s.hours !== undefined &&
          (await timeActions.update(first, {
            duration_hours: s.hours,
            ...(s.note ? { note: s.note } : {})
          }))
      );
    default:
      return false;
  }
};
